/**
 * People — Members and Roles under one surface.
 *
 * Members and roles are two views of the same question ("who can do what"),
 * and splitting them across separate nav entries makes an admin bounce
 * between pages to answer it. Every product that does this well — Notion,
 * Retool, Metabase — keeps them as tabs under one heading, so this does too.
 *
 * The tab lives in the URL (`/users`, `/users/roles`, `/users/shared`) rather than in
 * component state, so a link to a role's access rules is shareable and the
 * back button behaves.
 *
 * Each tab owns its own subtitle and primary action, because "Invite user"
 * and "New role" are different verbs on different objects — a shared header
 * would have to go vague to cover both.
 */
import React from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import GroupRounded from '@mui/icons-material/GroupRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import IosShareRounded from '@mui/icons-material/IosShareRounded';

import { PageTabs } from '../../ui-kit';
import { useCan } from '../../ui/PermissionsProvider';
import { useFeature } from '../../ui/FeaturesProvider';
import UsersPage from '../UsersPage';
import RolesTab from './RolesTab';
import SharedViewersTab from './SharedViewersTab';

type TabKey = 'members' | 'roles' | 'shared';

const PATHS: Record<TabKey, string> = { members: '/users', roles: '/users/roles', shared: '/users/shared' };

export default function PeoplePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Sharing is for workspace admins; so is the list of who things are shared with.
  const sharingOn = useFeature('sharing').enabled;
  const canShare = useCan('manage_workspace') && sharingOn;
  const active: TabKey = location.pathname.endsWith('/roles') ? 'roles'
    : location.pathname.endsWith('/shared') ? 'shared' : 'members';

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PageTabs
        ariaLabel="People"
        value={active}
        onChange={(value) => navigate(PATHS[value])}
        tabs={[
          { value: 'members', label: 'Members', icon: <GroupRounded fontSize="inherit" /> },
          { value: 'roles', label: 'Roles', icon: <ShieldRounded fontSize="inherit" /> },
          // People outside the workspace something is shared with, by link.
          ...(canShare ? [{ value: 'shared' as const, label: 'Viewers', icon: <IosShareRounded fontSize="inherit" /> }] : []),
        ]}
      />

      {active === 'roles' ? <RolesTab /> : active === 'shared' && canShare ? <SharedViewersTab /> : <UsersPage />}
    </Box>
  );
}
